/**
 * Wording for the merge preview's bullet list (`MergeAccountsDialog`).
 *
 * Its own module rather than a second export from the dialog: the dialog's
 * preview is gated behind a Radix Select that jsdom cannot drive, so the copy
 * would otherwise be untestable — and this is text describing row deletion,
 * which is exactly the text worth pinning.
 */
import type { AccountMergeReport } from '../../lib/types.ts';

/**
 * The duplicate counts are split by HOW the row was matched: an exact match
 * (shared provider reference) is a fact, a fuzzy one (amount + ±3 days + payee
 * similarity) is a guess — and this is the one branch that DELETES user data,
 * so the two must never share a number. Cross-channel duplicates (one IBAN via
 * EB and FinTS) are fuzzy by construction — `import_hash` mixes in the
 * account id and no FinTS row carries a provider reference — so the
 * „wahrscheinlich" line is the common case, not the exotic one.
 */
export function mergeReportLines(report: AccountMergeReport): string[] {
  const parts = [`${report.moved} Buchungen werden übernommen`];

  const exact = report.duplicates_deleted - report.duplicates_deleted_fuzzy;
  if (exact > 0) {
    parts.push(`${exact} sichere Duplikate werden gelöscht (gleiche Referenz)`);
  }
  if (report.duplicates_deleted_fuzzy > 0) {
    parts.push(
      `${report.duplicates_deleted_fuzzy} wahrscheinliche Duplikate werden gelöscht (gleicher Betrag, ±3 Tage, ähnlicher Empfänger; ohne Empfänger: gleicher Tag)`,
    );
  }
  // The remainder: the target's own copy is itself a tombstone, so the source
  // row is left alone rather than deleting the last live copy.
  const kept = report.duplicates - report.duplicates_deleted;
  if (kept > 0) parts.push(`${kept} Duplikate werden übersprungen`);

  if (report.enriched > 0) parts.push(`${report.enriched} davon ergänzen den Händlernamen`);
  if (report.contracts > 0) parts.push(`${report.contracts} Verträge ziehen um`);
  if (report.import_batches > 0) parts.push(`${report.import_batches} Import-Stapel ziehen um`);

  if (report.kept_as_source) {
    parts.push(
      'Das Quellkonto bleibt als zweite Datenquelle verbunden — künftige Buchungen landen direkt im Zielkonto',
    );
    if (report.balance_snapshots_deleted > 0) {
      parts.push(
        `${report.balance_snapshots_deleted} Kontostand-Verlaufspunkte des Quellkontos werden entfernt (das Zielkonto führt den Saldo)`,
      );
    }
  }
  return parts;
}
